import { context } from '@devvit/web/client';
import { useEffect, useState } from 'react';
import type { BrawdleAttributeKey } from '../../shared/types/api';

export type LocaleKey = 'en' | 'fr';

export type BrawdleStrings = {
  locale: LocaleKey;
  splash: {
    greeting: (username?: string | null) => string;
    heroTitle: string;
    description: string;
    rulesTitle: string;
    rules: string[];
    dayLabel: (day: number) => string;
    cta: string;
    links: {
      docs: string;
      subreddit: string;
      discord: string;
    };
  };
  app: {
    title: string;
    subtitle: (puzzleId: string) => string;
    description: (maxGuesses: number) => string;
    loading: string;
    errorLoading: string;
    retry: string;
    emptyGuesses: string;
    status: {
      restoring: string;
      won: string;
      lost: string;
      remaining: (count: number) => string;
    };
  };
  form: {
    inputLabel: string;
    placeholder: string;
    submit: string;
    submitting: string;
    missingInput: string;
  };
  autocomplete: {
    loading: string;
    empty: string;
  };
  share: {
    copy: string;
    copied: string;
    hintPlaying: string;
    hintFinished: string;
    copyLink: string;
    linkCopied: string;
    invite: string;
  };
  endModal: {
    titleWon: string;
    titleLost: string;
    subtitleWon: (tries: number) => string;
    subtitleLost: string;
    revealLabel: string;
    revealInline: string;
    close: string;
  };
  errors: {
    generic: string;
    puzzleMismatch: string;
    unknownCharacter: string;
    invalidBody: string;
  };
  guessRow: {
    attributeLabels: Record<BrawdleAttributeKey, string>;
  };
};

const STRINGS: Record<LocaleKey, BrawdleStrings> = {
  en: {
    locale: 'en',
    splash: {
      greeting: (username) => `Hey ${username ?? 'brawler'} 👋`,
      heroTitle: 'Welcome to',
      description:
        'Guess the Brawl Stars brawler of the day in 6 tries. Every guess reveals per-attribute hints like rarity, class, speed, range, reload and release year.',
      rulesTitle: 'Quick rules',
      rules: [
        'One puzzle per day (UTC).',
        '6 guesses max with color-coded feedback per attribute.',
        'Progress is saved locally and can be shared Wordle-style.',
      ],
      dayLabel: (day) => `Day #${day}`,
      cta: 'Play now',
      links: {
        docs: 'Docs',
        subreddit: 'r/Devvit',
        discord: 'Discord',
      },
    },
    app: {
      title: 'Brawdle',
      subtitle: (puzzleId) => `Puzzle for ${puzzleId}`,
      description: (maxGuesses) => `Guess the daily brawler in ${maxGuesses} tries or fewer.`,
      loading: "Loading today's puzzle...",
      errorLoading: 'Server is unavailable. Please try again shortly.',
      retry: 'Try again',
      emptyGuesses: 'No guesses yet.',
      status: {
        restoring: 'Restoring your progress...',
        won: 'Nice! You found today’s brawler.',
        lost: 'Game over. Come back tomorrow for a new puzzle.',
        remaining: (count) => `You have ${count} guess${count > 1 ? 'es' : ''} left.`,
      },
    },
    form: {
      inputLabel: 'Guess a brawler',
      placeholder: 'Search Shelly, Colt...',
      submit: 'Submit guess',
      submitting: 'Submitting...',
      missingInput: 'Type or pick a brawler first.',
    },
    autocomplete: {
      loading: 'Loading suggestions...',
      empty: 'No matches',
    },
    share: {
      copy: 'Copy result',
      copied: 'Copied!',
      hintPlaying: 'Share your current progress.',
      hintFinished: 'Share your final board.',
      copyLink: 'Copy puzzle link',
      linkCopied: 'Link copied!',
      invite: 'Share it with your clubmates:',
    },
    endModal: {
      titleWon: 'You did it!',
      titleLost: 'Nice try!',
      subtitleWon: (tries) => `Solved in ${tries} ${tries > 1 ? 'tries' : 'try'}.`,
      subtitleLost: 'Better luck tomorrow.',
      revealLabel: 'Today’s brawler was',
      revealInline: 'The brawler was',
      close: 'Close',
    },
    errors: {
      generic: 'Unable to validate your guess.',
      puzzleMismatch: "Puzzle expired. Refresh to play today's run.",
      unknownCharacter: 'Unknown brawler. Try another name.',
      invalidBody: 'Request is incomplete.',
    },
    guessRow: {
      attributeLabels: {
        rarity: 'Rarity',
        class: 'Class',
        movement: 'Speed',
        range: 'Range',
        reload: 'Reload',
        released: 'Year',
      },
    },
  },
  fr: {
    locale: 'fr',
    splash: {
      greeting: (username) => `Bonjour ${username ?? 'brawler'} 👋`,
      heroTitle: 'Bienvenue sur',
      description:
        'Devine la troupe de Brawl Stars du jour en 6 essais. Chaque tentative renvoie un indice par attribut : rareté, classe, vitesse, portée, recharge et année de sortie.',
      rulesTitle: 'Règles rapides',
      rules: [
        '1 puzzle par jour (UTC).',
        '6 essais maximum, un badge coloré par attribut.',
        'Progression sauvegardée localement, partage façon Wordle.',
      ],
      dayLabel: (day) => `Jour #${day}`,
      cta: 'Jouer maintenant',
      links: {
        docs: 'Docs',
        subreddit: 'r/Devvit',
        discord: 'Discord',
      },
    },
    app: {
      title: 'Brawdle',
      subtitle: (puzzleId) => `Puzzle du ${puzzleId}`,
      description: (maxGuesses) => `Devine le brawler du jour en ${maxGuesses} essais maximum.`,
      loading: 'Chargement du puzzle du jour...',
      errorLoading: 'Le serveur ne répond pas. Réessaie dans un instant.',
      retry: 'Réessayer',
      emptyGuesses: 'Aucun essai pour le moment.',
      status: {
        restoring: 'Restauration de ta progression...',
        won: 'Bravo ! Tu as trouvé le brawler du jour.',
        lost: 'Partie terminée. Reviens demain pour un nouveau puzzle.',
        remaining: (count) => `Il te reste ${count} essai${count > 1 ? 's' : ''}.`,
      },
    },
    form: {
      inputLabel: 'Devine un brawler',
      placeholder: 'Cherche Shelly, Colt...',
      submit: "Valider l'essai",
      submitting: 'Validation...',
      missingInput: 'Tape ou sélectionne un brawler.',
    },
    autocomplete: {
      loading: 'Chargement des suggestions...',
      empty: 'Aucun résultat',
    },
    share: {
      copy: 'Copier le résultat',
      copied: 'Copié !',
      hintPlaying: 'Partage ta progression en cours.',
      hintFinished: 'Partage ton résultat final.',
      copyLink: 'Copier le lien du puzzle',
      linkCopied: 'Lien copié !',
      invite: 'Partage ça avec ton club :',
    },
    endModal: {
      titleWon: 'Bravo !',
      titleLost: 'Ce n’était pas ça…',
      subtitleWon: (tries) => `Trouvé en ${tries} essai${tries > 1 ? 's' : ''}.`,
      subtitleLost: 'Reviens demain pour retenter ta chance.',
      revealLabel: 'Le brawler du jour était',
      revealInline: 'Le brawler était',
      close: 'Fermer',
    },
    errors: {
      generic: 'Impossible de valider ta tentative.',
      puzzleMismatch: 'Puzzle expiré. Actualise pour jouer celui du jour.',
      unknownCharacter: 'Brawler inconnu.',
      invalidBody: 'Requête incomplète.',
    },
    guessRow: {
      attributeLabels: {
        rarity: 'Rareté',
        class: 'Classe',
        movement: 'Vitesse',
        range: 'Portée',
        reload: 'Recharge',
        released: 'Année',
      },
    },
  },
};

const getContextLocale = (): string | undefined => {
  const locale = context.language;
  return typeof locale === 'string' ? locale : undefined;
};

const getNavigatorLocales = (): string[] => {
  if (typeof navigator === 'undefined') {
    return [];
  }
  const locales: string[] = [];
  if (Array.isArray(navigator.languages)) {
    locales.push(
      ...navigator.languages.filter(
        (lang): lang is string => typeof lang === 'string' && lang.trim().length > 0
      )
    );
  }
  if (typeof navigator.language === 'string' && navigator.language.trim().length > 0) {
    locales.push(navigator.language);
  }
  return locales;
};

const getUrlLocale = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const langParam = new URLSearchParams(window.location.search).get('lang');
  return typeof langParam === 'string' ? langParam : undefined;
};

const normalizeLocaleKey = (value?: string): LocaleKey =>
  value?.toLowerCase().startsWith('fr') ? 'fr' : 'en';

const detectLocale = (): LocaleKey => {
  const urlLocale = getUrlLocale();
  if (urlLocale) {
    return normalizeLocaleKey(urlLocale);
  }
  return 'en';
};

export const getCurrentStrings = (): BrawdleStrings => STRINGS[detectLocale()];

export const useLocaleStrings = (): BrawdleStrings => {
  const [locale, setLocale] = useState<LocaleKey>(() => detectLocale());

  useEffect(() => {
    setLocale(detectLocale());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleLanguageChange = () => {
      setLocale(detectLocale());
    };
    window.addEventListener('languagechange', handleLanguageChange);
    return () => window.removeEventListener('languagechange', handleLanguageChange);
  }, []);

  return STRINGS[locale];
};

